import { TranslateService } from '@ngx-translate/core';
import { Injectable } from '@angular/core';
import { CategoriesService, CategoryResponse, CollectionResponse, CollectionsService, ProductTypeResponse, ProductTypesService, RoleDetailsResponse, RolesService, SettingsService, RetailerResponse, RetailersService, BrandResponse } from '../backend/clients';
import { FilterType } from '@src/app/shared/components/table-filter/table-filter.component';
import { CountryService } from './countries-service';
import { DatePipe } from '@angular/common';
import { FriendlyBooleanPipe } from '@app/shared/pipes/friendlyBoolean.pipe';
import { ActiveInactivePipe } from '@app/shared/pipes/activeInactive.pipe';
import { ConcatenateStringPipe } from '@app/shared/pipes/concatenateString.pipe';
import { ImportStatePipe } from '@src/app/shared/pipes/importState.pipe';
import { ExportStatePipe } from '@src/app/shared/pipes/exportState.pipe';
import { ImportTypePipe } from '@app/shared/pipes/importType.pipe';
import { ExportTypePipe } from '@app/shared/pipes/exportType.pipe';
import { CardOrderingStatusPipe } from '@app/shared/pipes/cardOrderingStatus.pipe';
import { CardOrderingItemStatusPipe } from '@app/shared/pipes/cardOrderingItemStatus.pipe';
import { SessionService } from './session.service';
import { FilterOperator } from '@app/shared/enums/FilterOperator.enum';
import { firstValueFrom } from 'rxjs';
import { SeparateArrayByCommasPipe } from '@src/app/shared/pipes/separateArrayByCommas';
import { OrderGenerationTypePipe } from '@src/app/shared/pipes/orderGenerationType.pipe';
import { OrderStatePipe } from '@src/app/shared/pipes/orderState.pipe';
import { ItemLocationPipe } from '@src/app/shared/pipes/itemLocation.pipe';
import { AttributeDataTypePipe } from '@src/app/shared/pipes/attributeDataType.pipe';
import { LandingPagePipe } from '@app/shared/pipes/landingPage.pipe';

@Injectable({
  providedIn: 'root'
})
export class TableSettingService {
  public static SalesTableName = 'Sales';
  public static RetailsTableName = 'Retail';
  public static RetailerAttributeDefinitionsTableName = 'Retailer-AttributeDefinitions';
  public static WarrantiesTableName = 'Warranties';
  public static WarrantiesGlobalSearchTableName = 'WarrantiesGlobalSearch';
  public static RolesTableName = 'Roles';
  public static CodeOrdersTableName = 'Code-Order';
  public static ProductsTableName = 'Products';
  public static ItemsTableName = 'Items';
  public static InventoriesTableName = 'Inventories';
  public static PendingInventoriesTableName = 'PendingInventories';
  public static ShipmentsTableName = 'Shipment';
  public static CustomersTableName = 'Customers';
  public static UsersTableName = 'Users';
  public static UnregisteredUsersTableName = 'Unregistered-Users';
  public static ImportsTableName = 'Imports';
  public static CardOrdersTableName = 'Card-Ordering';
  public static ProductTypesTableName = 'Product-Types';
  public static ProductAttributeDefinitionsTableName = 'Product-AttributeDefinitions';
  public static ProductCustomizationsTableName = 'Product-Customizations';
  public static CollectionsTableName = 'Collections';
  public static CategoriesTableName = 'Categories';
  public static MarketsTableName = 'Markets';
  public static DistributionChannelsTableName = 'Distribution-Channels';
  public static ExportsTableName = 'Exports';
  public static RetailerTypesTableName = 'Retailer-Types';
  public static BusinessGroupsTableName = 'Business-Groups';
  public static MachineClientsTableName = 'Machine-Clients';
  public static SurveysTableName = 'Surveys';

  prefixTable: string = 'TABLE-';
  viewSettingsTables: string = 'VIEWSETTINGS-TABLES';
  tableSettings: Map<string, Column[]> = new Map<string, Column[]>();
  exportSettings: Map<string, Column[]> = new Map<string, Column[]>();

  private viewSettingsRequest: Promise<any[]> | null = null; // current pending request
  private filterOperator = FilterOperator;
  private friendlyBooleanPipe: FriendlyBooleanPipe;
  private activeInactivePipe: ActiveInactivePipe;
  private concatenateStringPipe: ConcatenateStringPipe;
  private importStatePipe: ImportStatePipe;
  private exportStatePipe: ExportStatePipe;
  private importTypePipe: ImportTypePipe;
  private exportTypePipe: ExportTypePipe;
  private cardOrderingStatusPipe: CardOrderingStatusPipe;
  private cardOrderingItemStatusPipe: CardOrderingItemStatusPipe;
  private separateArrayByCommasPipe: SeparateArrayByCommasPipe;
  private categories: CategoryResponse[] | null | undefined = [];
  private productTypes: ProductTypeResponse[] | null | undefined = [];
  private brands: BrandResponse[] | null | undefined = [];
  private collections: CollectionResponse[] | null | undefined = [];
  private roles: RoleDetailsResponse[] | null | undefined = [];
  private orderGenerationTypePipe: OrderGenerationTypePipe;
  private orderStatePipe: OrderStatePipe;
  private itemLocationPipe: ItemLocationPipe;
  private attributeDataTypePipe: AttributeDataTypePipe;
  private landingPagePipe: LandingPagePipe;
  private retailers: RetailerResponse[] | null | undefined = [];

  companyIsPrepaired = false;

  constructor(public settingsService: SettingsService,
    public countryService: CountryService,
    public categoriesService: CategoriesService,
    public retailersService: RetailersService,
    public productTypeService: ProductTypesService,
    public collectionsService: CollectionsService,
    public session: SessionService,
    public rolesService: RolesService,
    private translateService: TranslateService) {

    // Pipe initializers
    this.friendlyBooleanPipe = new FriendlyBooleanPipe(this.translateService);
    this.activeInactivePipe = new ActiveInactivePipe(this.translateService);
    this.concatenateStringPipe = new ConcatenateStringPipe(this.translateService);
    this.importStatePipe = new ImportStatePipe(this.translateService);
    this.exportStatePipe = new ExportStatePipe(this.translateService);
    this.importTypePipe = new ImportTypePipe(this.translateService);
    this.exportTypePipe = new ExportTypePipe(this.translateService);
    this.cardOrderingStatusPipe = new CardOrderingStatusPipe(this.translateService);
    this.cardOrderingItemStatusPipe = new CardOrderingItemStatusPipe(this.translateService);
    this.separateArrayByCommasPipe = new SeparateArrayByCommasPipe();
    this.orderGenerationTypePipe = new OrderGenerationTypePipe(this.translateService);
    this.orderStatePipe = new OrderStatePipe(this.translateService);
    this.itemLocationPipe = new ItemLocationPipe(this.translateService);
    this.attributeDataTypePipe = new AttributeDataTypePipe(this.translateService);
    this.landingPagePipe = new LandingPagePipe(this.translateService);

    this.session.currentCompanyData$.subscribe(company => {
      this.companyIsPrepaired = !!company?.isPrepaired;
    });
  }

  // Tables with async data in filters
  initProducts() {
    this.tableSettings.set(TableSettingService.ProductsTableName, [
      { field: 'name', header: 'Name', filter: FilterType.Text, operator: this.filterOperator.CONTAINS },
      { field: 'sku', header: 'Sku', filter: FilterType.Text, operator: this.filterOperator.CONTAINS },
      { field: 'productTypeName', filterField: 'productTypeId', header: 'ProductType', filter: FilterType.ComboCheckbox, panelFilter: FilterType.CustomDropdown, operator: this.filterOperator.IN, filterData: this.productTypes },
      { field: 'collections', filterField: 'collections.id', header: 'Collections', filter: FilterType.ComboCheckbox, panelFilter: FilterType.CustomDropdown, operator: this.filterOperator.IN, filterData: this.collections, sortable: false },
      { field: 'categories', filterField: 'categories.id', header: 'Categories', filter: FilterType.ComboCheckbox, panelFilter: FilterType.CustomDropdown, operator: this.filterOperator.IN, filterData: this.categories, sortable: false },
      { field: 'brandName', filterField: 'brandId', header: 'Brand', filter: FilterType.ComboCheckbox, panelFilter: FilterType.CustomDropdown, operator: this.filterOperator.IN, filterData: this.brands},
      { field: 'isActive', header: 'Status', filter: FilterType.ComboCheckbox, panelFilter: FilterType.CustomDropdown, operator: this.filterOperator.IN, formatType: FormatType.ActiveInactive, filterData: this.activeInactivePipe.values },
      { field: 'Actions', header: 'Actions' }
    ]);
  }

  initProductTypes() {
    this.tableSettings.set(TableSettingService.ProductTypesTableName, [
      { field: 'name', header: 'Attribute', filter: FilterType.Text, operator: this.filterOperator.CONTAINS },
      { field: 'code', header: 'Code', filter: FilterType.Text, operator: this.filterOperator.CONTAINS },
      { field: 'Actions', header: 'Actions' }
    ]);
  }

  initUsers() {
    this.tableSettings.set(TableSettingService.UsersTableName, [
      { field: 'firstName', header: 'FirstName', filter: FilterType.Text, operator: this.filterOperator.CONTAINS },
      { field: 'lastName', header: 'LastName', filter: FilterType.Text, operator: this.filterOperator.CONTAINS },
      { field: 'username', header: 'Username', filter: FilterType.Text, operator: this.filterOperator.CONTAINS },
      { field: 'roleName', filterField: 'roleId', header: 'UserRoles', filter: FilterType.ComboCheckbox, panelFilter: FilterType.CustomDropdown, operator: this.filterOperator.IN, filterData: this.roles },
      { field: 'latestLogin', header: 'LastLogin', filter: FilterType.Date, operator: this.filterOperator.IN_RANGE, formatType: FormatType.Date },
      { field: 'isActive', header: 'Status', filter: FilterType.ComboCheckbox, panelFilter: FilterType.CustomDropdown, operator: this.filterOperator.IN, formatType: FormatType.ActiveInactive, filterData: this.activeInactivePipe.values },
      { field: 'updatedBy.username', header: 'UpdatedBy', filter: FilterType.Text, operator: this.filterOperator.CONTAINS },
      { field: 'Actions', header: 'Actions' }
    ]);
  }

  async initUnregisteredUsers() {
    this.tableSettings.set(TableSettingService.UnregisteredUsersTableName, [
      { field: 'firstName', header: 'FirstName', filter: FilterType.Text, operator: this.filterOperator.CONTAINS },
      { field: 'lastName', header: 'LastName', filter: FilterType.Text, operator: this.filterOperator.CONTAINS },
      { field: 'romanisedFirstName', header: 'RomanisedFirstName', filter: FilterType.Text, operator: this.filterOperator.CONTAINS },
      { field: 'romanisedLastName', header: 'RomanisedLastName', filter: FilterType.Text, operator: this.filterOperator.CONTAINS },
      { field: 'retailers', filterField: 'retailers.id', header: 'Retailers', filter: FilterType.ComboCheckbox, panelFilter: FilterType.CustomDropdown, operator: this.filterOperator.IN, filterData: this.retailers, sortable: false },
      { field: 'Actions', header: 'Actions' }
    ]);
  }

  async saveTableSettings(tableName: string, columns: Column[], isExport: boolean = false) {

    // prevent users from hiding the Actions column
    const normalizedColumns = columns.map(column => ({
      ...column,
      hide: column.field === 'Actions' ? false : column.hide
    }));

    const payload: any = { name: this.prefixTable + tableName };

    if (isExport) {
      payload.exportSettings = JSON.stringify(normalizedColumns);
    } else {
      payload.settings = JSON.stringify(normalizedColumns);
    }

    await firstValueFrom(this.settingsService.apiSettingsViewUpsertPut(payload));
  }

  getTableSettings(tableName: string): Column[] {
    return this.tableSettings.get(tableName) ?? [];
  }

  getExportSettings(tableName: string): Column[] {
    return this.exportSettings.get(tableName) ?? [];
  }

  async getTableDefaults(tableName: string) {
    const columns: Column[] = [];
    const tableSettingsCurrent = this.tableSettings.get(tableName);

    let response = await this.getViewSettings();
    let serialized = response.find(x => x.name == this.prefixTable + tableName);
    let tableSettingsSaved: Column[];

    tableSettingsSaved = serialized?.settings
      ? JSON.parse(serialized.settings)
      : tableSettingsCurrent ?? [];

    tableSettingsCurrent?.forEach(column => {
      const setting = tableSettingsSaved.find(v => v.field == column.field);
      if (!setting?.hide) {
        columns.push(column);
      }
    });

    return columns;
  }

  async getAllTablesDefaultsSettings() {
    const response = await this.getViewSettings();
    localStorage.setItem(this.viewSettingsTables, JSON.stringify(response));
  }

  getTableDefaultsSettings(tableName: string) {
    const columns: Column[] = [];
    const tableSettingsCurrent = this.tableSettings.get(tableName);
    const storedTableSettings = localStorage.getItem(this.viewSettingsTables);
    if (!storedTableSettings) return columns;
    const tableSettings = JSON.parse(storedTableSettings);
    const serialized = tableSettings.find((x: any) => x.name === this.prefixTable + tableName);
    let tableSettingsSaved: Column[];
    tableSettingsSaved = serialized?.settings
      ? JSON.parse(serialized.settings)
      : tableSettingsCurrent ?? [];

    tableSettingsCurrent?.forEach(column => {
      const setting = tableSettingsSaved.find(v => v.field == column.field);
      column.hide = setting ? !!setting.hide : true;
      column.order = setting ? setting.order : 99;
      columns.push(column);
    });

    return columns;
  }

  // Share only the running/pending requests to avoid duplicate backend calls
  private async getViewSettings(): Promise<any[]> {
    if (!this.viewSettingsRequest) {
      this.viewSettingsRequest = firstValueFrom(this.settingsService.apiSettingsViewReadGet())
        .finally(() => {
          // Don't cache response so settings can be refreshed later!
          this.viewSettingsRequest = null;
        });
    }

    return this.viewSettingsRequest;
  }

  getExportDefaultsSettings(tableName: string) {
    const columns: Column[] = [];
    const exportSettingsCurrent = this.exportSettings.get(tableName);
    const storedTableSettings = localStorage.getItem(this.viewSettingsTables);
    if (!storedTableSettings) return columns;
    const tableSettings = JSON.parse(storedTableSettings);
    const serialized = tableSettings.find((x: any) => x.name === this.prefixTable + tableName);
    let exportSettingsSaved: Column[];

    exportSettingsSaved = serialized?.exportSettings
      ? JSON.parse(serialized.exportSettings)
      : exportSettingsCurrent ?? [];

    exportSettingsCurrent?.forEach(column => {
      const setting = exportSettingsSaved.find(v => v.field == column.field);
      column.hide = setting ? !!setting.hide : true;
      column.order = setting ? setting.order : 99;
      columns.push(column);
    });

    return columns;
  }

  getNestedValue(obj: any, path: string): any {
    const keys = path.split('.');
    let currentValue = obj;

    for (const key of keys) {
      if (currentValue && currentValue[key] !== undefined) {
        currentValue = currentValue[key];
        break;
      } else {
        return null;
      }
    }

    return currentValue;
  }

  getColumnValue(rowData: any, column: Column): any {
    let value = this.getNestedValue(rowData, column.field);

    switch (column.formatType) {
      case FormatType.Date:
        value = new DatePipe(this.session.locale).transform(value, 'dd-MM-yyyy');
        break;

      case FormatType.DateTime:
        value = new DatePipe(this.session.locale).transform(value, 'dd-MM-yyyy HH:mm');
        break;

      case FormatType.DiffDates: {
        const today = new Date();
        const diffDate = new Date(value);

        today.setHours(0, 0, 0, 0);
        diffDate.setHours(0, 0, 0, 0);

        const diffMs = today.getTime() - diffDate.getTime();
        value = Math.floor(diffMs / (1000 * 60 * 60 * 24));
        break;
      }

      case FormatType.Time:
        value = new DatePipe(this.session.locale).transform(value, 'HH:mm');
        break;

      case FormatType.Numeric:
        break;

      case FormatType.FriendlyBoolean:
        value = this.friendlyBooleanPipe.transform(value);
        break;

      case FormatType.ActiveInactive:
        value = this.activeInactivePipe.transform(value);
        break;

      case FormatType.Combo:
        value = column.filterData?.find((v: any) => v.id == value)?.name;
        break;

      case FormatType.Role:
        // async observable subscription
        value = value?.name;
        break;

      case FormatType.ConcatenateString:
        value = this.concatenateStringPipe.transform(value, column.appendString || '');
        break;

      case FormatType.ImportState:
        value = this.importStatePipe.transform(value);
        break;

      case FormatType.ImportType:
        value = this.importTypePipe.transform(value);
        break;

      case FormatType.ExportState:
        value = this.exportStatePipe.transform(value);
        break;

      case FormatType.ExportType:
        value = this.exportTypePipe.transform(value);
        break;

      case FormatType.CardOrderingStatus:
        value = this.cardOrderingStatusPipe.transform(value);
        break;

      case FormatType.CardOrderingItemStatus:
        value = this.cardOrderingItemStatusPipe.transform(value);
        break;

      case FormatType.OrderGenerationType:
        value = this.orderGenerationTypePipe.transform(value);
        break;

      case FormatType.OrderState:
        value = this.orderStatePipe.transform(value);
        break;

      case FormatType.ItemLocation:
        value = this.itemLocationPipe.transform(value);
        break;

      case FormatType.AttributeDataType:
        value = this.attributeDataTypePipe.transform(value);
        break;

      case FormatType.SeparateArrayByCommas:
        value = this.separateArrayByCommasPipe.transform(value, 'name');
        break;

        case FormatType.LandingPage:
        value = this.landingPagePipe.transform(value);
        break;

      default:
        break;
    }

    return value;
  }

  public async init() {

    // system settings table: roles
    this.tableSettings.set(TableSettingService.RolesTableName, [
      { field: 'name', header: 'Name', filter: FilterType.Text, operator: this.filterOperator.CONTAINS },
      { field: 'priority', header: 'Priority', filter: FilterType.Text, operator: this.filterOperator.EQUALS, formatType: FormatType.Numeric },
      { field: 'isActive', header: 'Status', filter: FilterType.ComboCheckbox, operator: this.filterOperator.IN, formatType: FormatType.ActiveInactive, filterData: this.activeInactivePipe.values },
      { field: 'landingPage', header: 'Landing Page', filter: FilterType.ComboCheckbox, operator: this.filterOperator.IN, formatType: FormatType.LandingPage, filterData: this.landingPagePipe.landingPages },
      { field: 'Actions', header: 'Actions' }
    ]);

    // system settings table: machine clients
    this.tableSettings.set(TableSettingService.MachineClientsTableName, [
      { field: 'name', header: 'Name', filter: FilterType.Text, operator: this.filterOperator.CONTAINS },
      { field: 'clientId', header: 'ClientId', filter: FilterType.Text, operator: this.filterOperator.EQUALS, formatType: FormatType.Numeric },
      { field: 'isActive', header: 'Status', filter: FilterType.ComboCheckbox, operator: this.filterOperator.IN, formatType: FormatType.ActiveInactive, filterData: this.activeInactivePipe.values },
      { field: 'Actions', header: 'Actions' }
    ]);


    if (this.companyIsPrepaired) {
      this.tableSettings.set(TableSettingService.WarrantiesTableName, [
        { field: 'code', header: 'WarrantyCode', filter: FilterType.Text, operator: this.filterOperator.CONTAINS },
        { field: 'itemSerialNumber', filterField: 'item.serialNumber', header: 'SerialNumber', filter: FilterType.Text, operator: this.filterOperator.CONTAINS },
        { field: 'isPrepaired', header: 'Prepaired', filter: FilterType.ComboCheckbox, panelFilter: FilterType.CustomDropdown, operator: this.filterOperator.IN, formatType: FormatType.FriendlyBoolean, filterData: this.friendlyBooleanPipe.values },
        { field: 'activationDate', header: 'StartDate', filter: FilterType.Date, operator: this.filterOperator.IN_RANGE, formatType: FormatType.Date },
        { field: 'endDate', header: 'EndDate', filter: FilterType.Date, operator: this.filterOperator.IN_RANGE, formatType: FormatType.Date },
        { field: 'isActive', header: 'Status', filter: FilterType.ComboCheckbox, panelFilter: FilterType.CustomDropdown, operator: this.filterOperator.IN, formatType: FormatType.ActiveInactive, filterData: this.activeInactivePipe.values },
        { field: 'hasD2C', header: 'HasD2C', filter: FilterType.ComboCheckbox, panelFilter: FilterType.CustomDropdown, operator: this.filterOperator.IN, formatType: FormatType.FriendlyBoolean, filterData: this.friendlyBooleanPipe.values },
        { field: 'Actions', header: 'Actions' }
      ]);
    } else {
      this.tableSettings.set(TableSettingService.WarrantiesTableName, [
        { field: 'code', header: 'WarrantyCode', filter: FilterType.Text, operator: this.filterOperator.CONTAINS },
        { field: 'itemSerialNumber', filterField: 'item.serialNumber', header: 'SerialNumber', filter: FilterType.Text, operator: this.filterOperator.CONTAINS },
        { field: 'activationDate', header: 'StartDate', filter: FilterType.Date, operator: this.filterOperator.IN_RANGE, formatType: FormatType.Date },
        { field: 'endDate', header: 'EndDate', filter: FilterType.Date, operator: this.filterOperator.IN_RANGE, formatType: FormatType.Date },
        { field: 'isActive', header: 'Status', filter: FilterType.ComboCheckbox, panelFilter: FilterType.CustomDropdown, operator: this.filterOperator.IN, formatType: FormatType.ActiveInactive, filterData: this.activeInactivePipe.values },
        { field: 'hasD2C', header: 'HasD2C', filter: FilterType.ComboCheckbox, panelFilter: FilterType.CustomDropdown, operator: this.filterOperator.IN, formatType: FormatType.FriendlyBoolean, filterData: this.friendlyBooleanPipe.values },
        { field: 'Actions', header: 'Actions' }
      ]);
    }

    if (this.companyIsPrepaired) {
      this.tableSettings.set(TableSettingService.WarrantiesGlobalSearchTableName, [
        { field: 'code', header: 'WarrantyCode', sortable: false },
        { field: 'serialNumber', header: 'SerialNumber', sortable: false },
        { field: 'isPrepaired', header: 'Prepaired', formatType: FormatType.FriendlyBoolean, sortable: false },
        { field: 'activationDate', header: 'StartDate', formatType: FormatType.Date, sortable: false },
        { field: 'endDate', header: 'EndDate', formatType: FormatType.Date, sortable: false },
        { field: 'isActive', header: 'Status', formatType: FormatType.ActiveInactive, sortable: false },
        { field: 'hasD2C', header: 'HasD2C', formatType: FormatType.FriendlyBoolean, sortable: false },
        { field: 'Actions', header: 'Actions', sortable: false }
      ]);
    } else {
      this.tableSettings.set(TableSettingService.WarrantiesGlobalSearchTableName, [
        { field: 'code', header: 'WarrantyCode', sortable: false },
        { field: 'serialNumber', header: 'SerialNumber', sortable: false },
        { field: 'activationDate', header: 'StartDate', formatType: FormatType.Date, sortable: false },
        { field: 'endDate', header: 'EndDate', formatType: FormatType.Date, sortable: false },
        { field: 'isActive', header: 'Status', formatType: FormatType.ActiveInactive, sortable: false },
        { field: 'hasD2C', header: 'HasD2C', formatType: FormatType.FriendlyBoolean, sortable: false },
        { field: 'Actions', header: 'Actions', sortable: false }
      ]);
    }

    this.tableSettings.set(TableSettingService.SalesTableName, [
      { field: 'itemSerialNumber', filterField: 'item.serialNumber', header: 'SerialNumber', filter: FilterType.Text, operator: this.filterOperator.CONTAINS },
      { field: 'productSku', filterField: 'item.product.sku', header: 'SKU', filter: FilterType.Text, operator: this.filterOperator.CONTAINS },
      { field: 'productName', filterField: 'item.product.name', header: 'Product', filter: FilterType.Text, operator: this.filterOperator.CONTAINS },
      { field: 'warrantyCode', filterField: 'warranty.code', header: 'WarrantyCode', filter: FilterType.Text, operator: this.filterOperator.CONTAINS },
      { field: 'marketName', filterField: 'retailer.market.name', header: 'Market', filter: FilterType.Text, operator: this.filterOperator.CONTAINS },
      { field: 'retailerName', filterField: 'retailer.name', header: 'Retailer', filter: FilterType.Text, operator: this.filterOperator.CONTAINS },
      { field: 'brandName', filterField: 'item.product.brandId', header: 'Brand', filter: FilterType.ComboCheckbox, panelFilter: FilterType.CustomDropdown, operator: this.filterOperator.IN, filterData: this.brands},
      { field: 'date', header: 'Date', filter: FilterType.Date, operator: this.filterOperator.IN_RANGE, formatType: FormatType.Date },
      { field: 'isReturned', header: 'Returned', filter: FilterType.ComboCheckbox, panelFilter: FilterType.CustomDropdown, operator: this.filterOperator.IN, formatType: FormatType.FriendlyBoolean, filterData: this.friendlyBooleanPipe.values },
      { field: 'Actions', header: 'Actions' }
    ]);

    this.tableSettings.set(TableSettingService.RetailsTableName, [
      { field: 'code', header: 'Code', filter: FilterType.Text, operator: this.filterOperator.CONTAINS },
      { field: 'name', header: 'Name', filter: FilterType.Text, operator: this.filterOperator.CONTAINS },
      // { field: 'street', header: 'Street', filter: FilterType.Text, operator: this.filterOperator.CONTAINS },
      // { field: 'zip', header: 'Zip', filter: FilterType.Text, operator: this.filterOperator.EQUALS },
      { field: 'city', header: 'City', filter: FilterType.Text, operator: this.filterOperator.CONTAINS },
      { field: 'stateProvince', filterField: 'stateProvince', header: 'StateProvince', filter: FilterType.Text, operator: this.filterOperator.CONTAINS },
      { field: 'isoCountry', header: 'Country', filter: FilterType.ComboCheckbox, panelFilter: FilterType.CustomDropdown, operator: this.filterOperator.IN, filterData: await this.countryService.getCountries(), formatType: FormatType.Combo },
      { field: 'market', filterField: 'market.name', header: 'Market', filter: FilterType.Text, operator: this.filterOperator.CONTAINS },
      { field: 'brands', filterField: 'brands.id', header: 'Brands', filter: FilterType.ComboCheckbox, panelFilter: FilterType.CustomDropdown, operator: this.filterOperator.IN, filterData: this.brands, sortable: false },
      { field: 'isActive', header: 'Status', filter: FilterType.ComboCheckbox, panelFilter: FilterType.CustomDropdown, operator: this.filterOperator.IN, formatType: FormatType.ActiveInactive, filterData: this.activeInactivePipe.values },
      { field: 'Actions', header: 'Actions' }
    ]);

    this.tableSettings.set(TableSettingService.RetailerAttributeDefinitionsTableName, [
      { field: 'definitionCode', header: 'Code', filter: FilterType.Text, operator: this.filterOperator.CONTAINS },
      { field: 'retailerAttributeDataType', filterField: 'attributeDataType', header: 'DataType', filter: FilterType.ComboCheckbox, panelFilter: FilterType.CustomDropdown, operator: this.filterOperator.IN, formatType: FormatType.AttributeDataType, filterData: this.attributeDataTypePipe.attributeDataTypes },
      { field: 'allowMultipleValues', header: 'AllowMultipleValues', filter: FilterType.ComboCheckbox, panelFilter: FilterType.CustomDropdown, operator: this.filterOperator.IN, formatType: FormatType.FriendlyBoolean, filterData: this.friendlyBooleanPipe.values },
      { field: 'changesOverTime', header: 'ChangesOverTime', filter: FilterType.ComboCheckbox, panelFilter: FilterType.CustomDropdown, operator: this.filterOperator.IN, formatType: FormatType.FriendlyBoolean, filterData: this.friendlyBooleanPipe.values },
      { field: 'Actions', header: 'Actions' }
    ]);

    this.tableSettings.set(TableSettingService.ItemsTableName, [
      { field: 'productName', filterField: 'product.name', header: 'ProductName', filter: FilterType.Text, operator: this.filterOperator.CONTAINS },
      { field: 'productSku', filterField: 'product.sku', header: 'ProductSku', filter: FilterType.Text, operator: this.filterOperator.CONTAINS },
      { field: 'serialNumber', header: 'SerialNumber', filter: FilterType.Text, operator: this.filterOperator.CONTAINS },
      { field: 'isGenuine', header: 'Genuine', filter: FilterType.ComboCheckbox, panelFilter: FilterType.CustomDropdown, operator: this.filterOperator.IN, formatType: FormatType.FriendlyBoolean, filterData: this.friendlyBooleanPipe.values },
      { field: 'isCpo', header: 'CPO', filter: FilterType.ComboCheckbox, panelFilter: FilterType.CustomDropdown, operator: this.filterOperator.IN, formatType: FormatType.FriendlyBoolean, filterData: this.friendlyBooleanPipe.values },
      { field: 'disposition', header: 'Disposition', filter: FilterType.ComboCheckbox, panelFilter: FilterType.CustomDropdown, operator: this.filterOperator.IN, formatType: FormatType.ItemLocation, filterData: this.itemLocationPipe.itemLocationTypes },
      { field: 'brandName', filterField: 'product.brandId', header: 'Brand', filter: FilterType.ComboCheckbox, panelFilter: FilterType.CustomDropdown, operator: this.filterOperator.IN, filterData: this.brands},
      { field: 'Actions', header: 'Actions' }
    ]);

    this.tableSettings.set(TableSettingService.InventoriesTableName, [
      { field: 'name', header: 'Reference', filter: FilterType.Text, operator: this.filterOperator.CONTAINS },
      { field: 'createdOn', header: 'CreationDate', filter: FilterType.Date, operator: this.filterOperator.IN_RANGE, formatType: FormatType.Date },
      { field: 'updatedOn', header: 'LastUpdate', filter: FilterType.Date, operator: this.filterOperator.IN_RANGE, formatType: FormatType.Date },
      { field: 'retailerName', filterField: 'retailer.name', header: 'Retailer', filter: FilterType.Text, operator: this.filterOperator.CONTAINS },
      { field: 'brandName', filterField: 'brandId', header: 'Brand', filter: FilterType.ComboCheckbox, panelFilter: FilterType.CustomDropdown, operator: this.filterOperator.IN, filterData: this.brands},
      { field: 'totalItems', header: 'TotalItems', filter: FilterType.Text, operator: this.filterOperator.EQUALS, sortable: false },
      { field: 'isInvalid', header: 'IsInvalid', filter: FilterType.ComboCheckbox, panelFilter: FilterType.CustomDropdown, operator: this.filterOperator.IN, formatType: FormatType.FriendlyBoolean, filterData: this.friendlyBooleanPipe.values},
      { field: 'Actions', header: 'Actions' }
    ]);

    this.tableSettings.set(TableSettingService.PendingInventoriesTableName, [
      { field: 'name', header: 'Retailer', filter: FilterType.Text, operator: this.filterOperator.CONTAINS },
      { field: 'lastInventoryCreationDate', header: 'LastInventoryCreationDate', filter: FilterType.Date, operator: this.filterOperator.IN_RANGE, formatType: FormatType.Date },
      { field: 'lastInventoryCreationDate', header: 'InventoryDays', filter: FilterType.None, operator: this.filterOperator.EQUALS, formatType: FormatType.DiffDates, sortable: false },
      { field: 'Actions', header: 'Actions' }
    ]);

    this.tableSettings.set(TableSettingService.ShipmentsTableName, [
      { field: 'name', header: 'Reference', filter: FilterType.Text, operator: this.filterOperator.CONTAINS },
      { field: 'createdOn', header: 'CreationDate', filter: FilterType.Date, operator: this.filterOperator.IN_RANGE, formatType: FormatType.Date },
      { field: 'totalItems', header: 'TotalItems', filter: FilterType.Text, operator: this.filterOperator.EQUALS },
      { field: 'originRetailerName', filterField: 'originRetailer.name', header: 'Origin', filter: FilterType.Text, operator: this.filterOperator.EQUALS },
      { field: 'destinationRetailerName', filterField: 'destinationRetailer.name', header: 'Destination', filter: FilterType.Text, operator: this.filterOperator.EQUALS },
      { field: 'isCancelled', header: 'Cancelled', filter: FilterType.ComboCheckbox, panelFilter: FilterType.CustomDropdown, operator: this.filterOperator.IN, formatType: FormatType.FriendlyBoolean, filterData: this.friendlyBooleanPipe.values },
      { field: 'isReceived', header: 'Received', filter: FilterType.ComboCheckbox, panelFilter: FilterType.CustomDropdown, operator: this.filterOperator.IN, formatType: FormatType.FriendlyBoolean, filterData: this.friendlyBooleanPipe.values },
      { field: 'Actions', header: 'Actions' }
    ]);

    this.tableSettings.set(TableSettingService.CustomersTableName, [
      { field: 'firstName', header: 'FirstName', filter: FilterType.Text, operator: this.filterOperator.CONTAINS },
      { field: 'lastName', header: 'LastName', filter: FilterType.Text, operator: this.filterOperator.CONTAINS },
      { field: 'email', header: 'Email', filter: FilterType.Text, operator: this.filterOperator.CONTAINS },
      { field: 'isoCountry', header: 'Country', filter: FilterType.ComboCheckbox, panelFilter: FilterType.CustomDropdown, operator: this.filterOperator.IN, filterData: await this.countryService.getCountries(), formatType: FormatType.Combo },
      { field: 'createdOn', header: 'ExportCreationDate', filter: FilterType.Date, operator: this.filterOperator.IN_RANGE, formatType: FormatType.Date },
      { field: 'Actions', header: 'Actions' }
    ]);

    this.tableSettings.set(TableSettingService.CollectionsTableName, [
      { field: 'name', header: 'Name', filter: FilterType.Text, operator: this.filterOperator.CONTAINS },
      { field: 'code', header: 'Code', filter: FilterType.Text, operator: this.filterOperator.CONTAINS },
      { field: 'Actions', header: 'Actions' }
    ]);

    this.tableSettings.set(TableSettingService.CategoriesTableName, [
      { field: 'name', header: 'Name', filter: FilterType.Text, operator: this.filterOperator.CONTAINS },
      { field: 'code', header: 'Code', filter: FilterType.Text, operator: this.filterOperator.CONTAINS },
      { field: 'Actions', header: 'Actions' }
    ]);

    this.tableSettings.set(TableSettingService.ProductAttributeDefinitionsTableName, [
      { field: 'definitionCode', header: 'Code', filter: FilterType.Text, operator: this.filterOperator.CONTAINS },
      { field: 'productAttributeDataType', filterField: 'attributeDataType', header: 'DataType', filter: FilterType.ComboCheckbox, panelFilter: FilterType.CustomDropdown, operator: this.filterOperator.IN, formatType: FormatType.AttributeDataType, filterData: this.attributeDataTypePipe.attributeDataTypes },
      { field: 'allowMultipleValues', header: 'AllowMultipleValues', filter: FilterType.ComboCheckbox, panelFilter: FilterType.CustomDropdown, operator: this.filterOperator.IN, formatType: FormatType.FriendlyBoolean, filterData: this.friendlyBooleanPipe.values },
      { field: 'changesOverTime', header: 'ChangesOverTime', filter: FilterType.ComboCheckbox, panelFilter: FilterType.CustomDropdown, operator: this.filterOperator.IN, formatType: FormatType.FriendlyBoolean, filterData: this.friendlyBooleanPipe.values },
      { field: 'Actions', header: 'Actions' }
    ]);

    this.tableSettings.set(TableSettingService.ProductCustomizationsTableName, [
      { field: 'productName', filterField: 'product.name', header: 'ProductName', filter: FilterType.Text, operator: this.filterOperator.CONTAINS },
      { field: 'productSku', filterField: 'product.sku', header: 'ProductSku', filter: FilterType.Text, operator: this.filterOperator.CONTAINS },
      { field: 'name', header: 'CustomisationName', filter: FilterType.Text, operator: this.filterOperator.CONTAINS },
      { field: 'Actions', header: 'Actions' }
    ]);

    this.tableSettings.set(TableSettingService.MarketsTableName, [
      { field: 'name', header: 'Name', filter: FilterType.Text, operator: this.filterOperator.CONTAINS },
      { field: 'code', header: 'Code', filter: FilterType.Text, operator: this.filterOperator.CONTAINS },
      { field: 'Actions', header: 'Actions' }
    ]);

    this.tableSettings.set(TableSettingService.RetailerTypesTableName, [
      { field: 'name', header: 'Name', filter: FilterType.Text, operator: this.filterOperator.CONTAINS },
      { field: 'code', header: 'Code', filter: FilterType.Text, operator: this.filterOperator.CONTAINS },
      { field: 'Actions', header: 'Actions' }
    ]);

    this.tableSettings.set(TableSettingService.DistributionChannelsTableName, [
      { field: 'name', header: 'Name', filter: FilterType.Text, operator: this.filterOperator.CONTAINS },
      { field: 'code', header: 'Code', filter: FilterType.Text, operator: this.filterOperator.CONTAINS },
      { field: 'Actions', header: 'Actions' }
    ]);

    this.tableSettings.set(TableSettingService.ImportsTableName, [
      { field: 'name', header: 'ImportName', filter: FilterType.Text, operator: this.filterOperator.CONTAINS },
      { field: 'type', header: 'ImportType', filter: FilterType.ComboCheckbox, panelFilter: FilterType.CustomDropdown, operator: this.filterOperator.IN, formatType: FormatType.ImportType, filterData: this.importTypePipe.importTypesList },
      { field: 'createdBy', header: 'CreatedBy', filter: FilterType.Text, operator: this.filterOperator.CONTAINS },
      { field: 'state', header: 'ImportState', filter: FilterType.ComboCheckbox, panelFilter: FilterType.CustomDropdown, operator: this.filterOperator.IN, formatType: FormatType.ImportState, filterData: this.importStatePipe.importStatesList },
      { field: 'Actions', header: 'Actions' }
    ]);

    this.tableSettings.set(TableSettingService.ExportsTableName, [
      { field: 'requestedDate', header: 'ExportCreationDate', filter: FilterType.Date, operator: this.filterOperator.IN_RANGE, formatType: FormatType.DateTime },
      { field: 'name', header: 'ExportName', filter: FilterType.Text, operator: this.filterOperator.CONTAINS },
      { field: 'type', header: 'ExportType', filter: FilterType.ComboCheckbox, panelFilter: FilterType.CustomDropdown, operator: this.filterOperator.IN, formatType: FormatType.ExportType, filterData: this.exportTypePipe.exportTypesList },
      { field: 'createdBy', header: 'ExportCreatedBy', filter: FilterType.Text, operator: this.filterOperator.CONTAINS },
      { field: 'state', header: 'ExportState', filter: FilterType.ComboCheckbox, panelFilter: FilterType.CustomDropdown, operator: this.filterOperator.IN, formatType: FormatType.ExportState, filterData: this.exportStatePipe.exportStatesList },
      { field: 'Actions', header: 'Actions' }
    ]);

    this.tableSettings.set(TableSettingService.CardOrdersTableName, [
      { field: 'createdOn', header: 'CreationDate', filter: FilterType.Date, operator: this.filterOperator.IN_RANGE, formatType: FormatType.Date },
      { field: 'retailerName', filterField: 'retailer.name', header: 'Retailer', filter: FilterType.Text, operator: this.filterOperator.CONTAINS },
      { field: 'cardOrderingStatus', header: 'CardOrderState', filter: FilterType.ComboCheckbox, panelFilter: FilterType.CustomDropdown, operator: this.filterOperator.IN, formatType: FormatType.CardOrderingStatus, filterData: this.cardOrderingStatusPipe.cardOrderingStatusList },
      { field: 'Actions', header: 'Actions' }
    ]);

    this.tableSettings.set(TableSettingService.CodeOrdersTableName, [
      { field: 'id', header: 'OrderId', filter: FilterType.Text, operator: this.filterOperator.EQUALS },
      { field: 'createdOn', header: 'CreationDate', filter: FilterType.Date, operator: this.filterOperator.IN_RANGE, formatType: FormatType.Date },
      { field: 'codeType', header: 'OrderType', filter: FilterType.ComboCheckbox, panelFilter: FilterType.CustomDropdown, operator: this.filterOperator.IN, formatType: FormatType.OrderGenerationType, filterData: this.orderGenerationTypePipe.orderGenerationTypesList },
      { field: 'quantity', header: 'Quantity', filter: FilterType.Numeric, operator: this.filterOperator.EQUALS },
      { field: 'state', header: 'CodeOrderState', filter: FilterType.ComboCheckbox, panelFilter: FilterType.CustomDropdown, operator: this.filterOperator.IN, formatType: FormatType.OrderState, filterData: this.orderStatePipe.orderStatesList },
      { field: 'Actions', header: 'Actions' }
    ]);

    this.tableSettings.set(TableSettingService.BusinessGroupsTableName, [
      { field: 'name', header: 'Name', filter: FilterType.Text, operator: this.filterOperator.CONTAINS },
      { field: 'code', header: 'Code', filter: FilterType.Text, operator: this.filterOperator.CONTAINS },
      { field: 'Actions', header: 'Actions' }
    ]);

    this.tableSettings.set(TableSettingService.SurveysTableName, [
      { field: 'name', header: 'Name', filter: FilterType.Text, operator: this.filterOperator.CONTAINS },
      // { field: 'isAnonymous', header: 'IsAnonymous', filter: FilterType.ComboCheckbox, panelFilter: FilterType.CustomDropdown, operator: this.filterOperator.IN, formatType: FormatType.FriendlyBoolean, filterData: this.friendlyBooleanPipe.values },
      { field: 'isPublished', header: 'IsPublished', filter: FilterType.ComboCheckbox, panelFilter: FilterType.CustomDropdown, operator: this.filterOperator.IN, formatType: FormatType.FriendlyBoolean, filterData: this.friendlyBooleanPipe.values },
      { field: 'isActive', header: 'Status', filter: FilterType.ComboCheckbox, panelFilter: FilterType.CustomDropdown, operator: this.filterOperator.IN, formatType: FormatType.ActiveInactive, filterData: this.activeInactivePipe.values },
      { field: 'Actions', header: 'Actions' }
    ]);
  }

  public initExports() {

    this.exportSettings.set(TableSettingService.SalesTableName, [
      {field: 'Type', header: 'Type', order: 99 },
      {field: 'Date', header: 'Date', order: 99 },
      {field: 'Price', header: 'Price', order: 99 },
      {field: 'RetailerCode', header: 'Retailer Code', order: 99 },
      {field: 'RetailerName', header: 'Retailer Name', order: 99 },
      {field: 'RetailerTypeName', header: 'Retailer Type Name', order: 99 },
      {field: 'MarketName', header: 'Market Name', order: 99 },
      {field: 'ParentMarketName1', header: 'Parent Market Name 1', order: 99 },
      {field: 'ParentMarketName2', header: 'Parent Market Name 2', order: 99 },
      {field: 'ParentMarketName3', header: 'Parent Market Name 3', order: 99 },
      {field: 'ParentMarketName4', header: 'Parent Market Name 4', order: 99 },
      {field: 'SerialNumber', header: 'Serial Number', order: 99 },
      {field: 'IsCpo', header: 'Is Cpo', order: 99 },
      {field: 'IsGenuine', header: 'Is Genuine', order: 99 },
      {field: 'ProductSku', header: 'Product Sku', order: 99 },
      {field: 'ProductName', header: 'Product Name', order: 99 },
      {field: 'ProductDescription', header: 'Product Description', order: 99 },
      {field: 'ProductTypeName', header: 'Product Type Name', order: 99 },
      {field: 'CategoryName', header: 'Category Name', order: 99 },
      {field: 'ParentCategoryName1', header: 'Parent Category Name 1', order: 99 },
      {field: 'ParentCategoryName2', header: 'Parent Category Name 2', order: 99 },
      {field: 'ParentCategoryName3', header: 'Parent Category Name 3', order: 99 },
      {field: 'ParentCategoryName4', header: 'Parent Category Name 4', order: 99 },
      {field: 'CollectionName', header: 'Collection Name', order: 99 },
      {field: 'ParentCollectionName1', header: 'Parent Collection Name 1', order: 99 },
      {field: 'ParentCollectionName2', header: 'Parent Collection Name 2', order: 99 },
      {field: 'ParentCollectionName3', header: 'Parent Collection Name 3', order: 99 },
      {field: 'ParentCollectionName4', header: 'Parent Collection Name 4', order: 99 },
      {field: 'UserName', header: 'User Name', order: 99 },
      {field: 'SoldBy', header: 'Sold By', order: 99 },
      {field: 'WarrantyCode', header: 'Warranty Code', order: 99 },
      {field: 'ActivationDate', header: 'Activation Date', order: 99 },
      {field: 'EndDate', header: 'End Date', order: 99 },
      {field: 'NumberOfExtensions', header: 'Number Of Extensions', order: 99 },
      {field: 'Lifecycle', header: 'Lifecycle', order: 99 }
    ]);

    this.exportSettings.set(TableSettingService.RetailsTableName, [
      { field: 'code', header: 'Code', order: 99 },
      { field: 'name', header: 'Name', order: 99 },
      { field: 'address', header: 'Address', order: 99 },
      { field: 'zip', header: 'Zip', order: 99 },
      { field: 'city', header: 'City', order: 99 },
      { field: 'isoCountry', header: 'Country', order: 99 },
      { field: 'latitude', header: 'Latitude', order: 99 },
      { field: 'longitude', header: 'Longitude', order: 99 },
      { field: 'isActive', header: 'Is Active', order: 99 },
      { field: 'market', header: 'Market', order: 99 },
      { field: 'marketCode', header: 'Market Code', order: 99 },
      { field: 'nameRomanised', header: 'Name Romanised', order: 99 },
      { field: 'cityRomanised', header: 'City Romanised', order: 99 },
      { field: 'hasCpo', header: 'Has CPO', order: 99 },
      { field: 'channel', header: 'Channel', order: 99 },
      { field: 'retailerType', header: 'Retailer Type', order: 99 },
      { field: 'externalRetailerId', header: 'External Retailer ID', order: 99 },
      { field: 'businessGroup', header: 'Business Group', order: 99 },
      { field: 'brand', header: 'Brand', order: 99 },
      { field: 'contact', header: 'Contact', order: 99 },
      { field: 'createdOn', header: 'Created on', order: 99 }
    ]);

    this.exportSettings.set(TableSettingService.ItemsTableName, [
      { field: 'productName', header: 'Product Name', order: 99 },
      { field: 'serialNumber', header: 'SerialNumber', order: 99 },
      { field: 'sku', header: 'SKU', order: 99 },
      { field: 'warrantyCode', header: 'Warranty Code', order: 99 },
      { field: 'brandName', header: 'Brand', order: 99 },
      { field: 'isCpo', header: 'Is CPO', order: 99 },
      { field: 'location', header: 'Is Sold', order: 99 },
      { field: 'Category', header: 'Category', order: 99 },
      { field: 'Collection', header: 'Collection', order: 99 },
      { field: 'CreatedOn', header: 'Created on', order: 99 }
    ]);

    this.exportSettings.set(TableSettingService.InventoriesTableName, [
      { field: 'inventoryReference', header: 'Reference', order: 99 },
      { field: 'retailerCode', header: 'Retailer Code', order: 99 },
      { field: 'retailerName', header: 'Retailer Name', order: 99 },
      { field: 'brandName', header: 'Brand', order: 99 },
      { field: 'createdBy', header: 'Created by', order: 99 },
      { field: 'createdOn', header: 'Created on', order: 99 },
      { field: 'totalItems', header: 'Total Items', order: 99 },
      { field: 'updatedBy', header: 'Updated by', order: 99 },
      { field: 'updatedOn', header: 'Updated on', order: 99 }
    ]);

    this.exportSettings.set(TableSettingService.ShipmentsTableName, [
      { field: 'shipmentReference', header: 'Reference', order: 99 },
      { field: 'retailerCode', header: 'Retailer Code', order: 99 },
      { field: 'retailerName', header: 'Retailer Name', order: 99 },
      { field: 'originCode', header: 'Origin Code', order: 99 },
      { field: 'originName', header: 'Origin Name', order: 99 },
      { field: 'destinationCode', header: 'Destination Code', order: 99 },
      { field: 'destinationName', header: 'Destination Name', order: 99 },
      { field: 'createdBy', header: 'Created by', order: 99 },
      { field: 'createdOn', header: 'Created on', order: 99 },
      { field: 'totalItems', header: 'Total Items', order: 99 },
      { field: 'updatedBy', header: 'Updated by', order: 99 },
      { field: 'updatedOn', header: 'Updated on', order: 99 }
    ]);

    this.exportSettings.set(TableSettingService.CustomersTableName, [
      { field: 'firstName', header: 'FirstName', order: 99 },
      { field: 'lastName', header: 'LastName', order: 99 },
      { field: 'email', header: 'Email', order: 99 },
      { field: 'gender', header: 'Gender', order: 99 },
      { field: 'birthdate', header: 'BirthDate', order: 99 },
      { field: 'isoCountry', header: 'Country', order: 99 },
      { field: 'address', header: 'Address', order: 99 },
      { field: 'postalCode', header: 'Postal Code', order: 99 },
      { field: 'foreign', header: 'Foreign', order: 99 },
      { field: 'wantEmails', header: 'WantEmails', order: 99 },
      { field: 'language', header: 'Language', order: 99 },
      { field: 'numberOfItemsPurchased', header: 'Number Of Items Purchased', order: 99 },
      { field: 'LastProductSKUPurchased', header: 'Last Product SKU Purchased', order: 99 },
      { field: 'LastProductNamePurchased', header: 'Last Product Name Purchased', order: 99 },
    ]);


    this.exportSettings.set(TableSettingService.CardOrdersTableName, [
      { field: 'createdOn', header: 'Creation Date', order: 99 },
      { field: 'createdBy', header: 'Created By', order: 99 },
      { field: 'retailerCode', header: 'Retailer Code', order: 99 },
      { field: 'retailerName', header: 'Retailer Name', order: 99 },
      { field: 'CardOrderingStatus', header: 'Status', order: 99 },
      { field: 'CardOrderingItems', header: 'Items', order: 99 }
    ]);

    this.exportSettings.set(TableSettingService.UsersTableName, [
      { field: 'firstName', header: 'FirstName', order: 99 },
      { field: 'lastName', header: 'LastName', order: 99 },
      { field: 'username', header: 'Username', order: 99 },
      { field: 'email', header: 'Email', order: 99 },
      { field: 'role', header: 'Role', order: 99 },
      { field: 'isActive', header: 'Status', order: 99 },
      { field: 'retailercodes', header: 'Retailer Codes', order: 99 },
      { field: 'marketcodes', header: 'Market Codes', order: 99 },
      { field: 'updatedBy', header: 'Updated By', order: 99 }
    ]);
  }
}


export interface Column {
  field: string;
  header: string;
  sortField?: string,
  sortable?: boolean;
  filter?: FilterType;
  panelFilter?: FilterType;
  filterField?: string;
  filterData?: any;
  comboData?: any;
  operator?: string;
  filterPlaceHolder?: string,
  hide?: boolean;
  formatType?: FormatType;
  appendString?: string;
  order?: number;
}

export enum FormatType {
  Text,
  Numeric,
  Date,
  DateTime,
  DiffDates,
  Time,
  Combo,
  FriendlyBoolean,
  ActiveInactive,
  Role,
  ConcatenateString,
  ImportState,
  ImportType,
  ExportState,
  ExportType,
  CardOrderingStatus,
  CardOrderingItemStatus,
  SeparateArrayByCommas,
  OrderGenerationType,
  OrderState,
  ItemLocation,
  AttributeDataType,
  LandingPage
}

export interface ValueListPair {
  label: string;
  value: string;
}
